import Box from '@mui/material/Box';
import { styled, css } from '@mui/material/styles';
import React from 'react';

import Button from '~/client/components/base/button';
import Chip from '~/client/components/base/chip';
import { getDesignTokens } from '~/client/components/base/core';
import {
  GenericTableNew,
  TableColumnNew as TableColumn,
} from '~/client/components/base/generic-table';
import { ArrowRight } from '~/client/components/base/icon';
import Text from '~/client/components/base/text';
import {
  aiSystemBundleRouteUtil,
  functionalModelBundleRouteUtil,
  candidateModelBundleRouteUtil,
  candidateAgentBundleRouteUtil,
  vendorModelBundleRouteUtil,
  vendorAgentBundleRouteUtil,
} from '~/client/routes';
import * as GQL from '~/graphql/generated/client';

interface ControlTaskTableViewProps {
  tasks: GQL.ControlTask[];
  className?: string;
}

const controlStatusLabel = (status: GQL.ControlAssignmentStatus): string => {
  switch (status) {
    case GQL.ControlAssignmentStatus.Completed:
      return 'Completed';
    case GQL.ControlAssignmentStatus.Cancelled:
      return 'Cancelled';
    default:
      return 'Pending';
  }
};

const ControlTaskTableViewImpl: React.FC<ControlTaskTableViewProps> = ({
  tasks,
  className,
}) => {
  const aiSystemBundleHelper = aiSystemBundleRouteUtil.useHelper();
  const functionalModelBundleHelper =
    functionalModelBundleRouteUtil.useHelper();
  const candidateModelBundleHelper = candidateModelBundleRouteUtil.useHelper();
  const candidateAgentBundleHelper = candidateAgentBundleRouteUtil.useHelper();
  const vendorModelBundleHelper = vendorModelBundleRouteUtil.useHelper();
  const vendorAgentBundleHelper = vendorAgentBundleRouteUtil.useHelper();

  const handleRowClick = (task: GQL.ControlTask) => {
    // Deep-link straight to the bundle form for the exact question, scrolled-to
    // and highlighted via the existing `?highlight=<controlIdentifier>` mechanism
    // (mirrors the notification-bell navigation).
    const highlight = `?highlight=${encodeURIComponent(task.controlIdentifier)}`;
    const state = { from: 'tasks' };
    try {
      switch (task.projectType) {
        case GQL.ProjectType.Organization:
        case GQL.ProjectType.AiSystem:
          aiSystemBundleHelper.navigateTo(
            'BUNDLE_FORM',
            {
              params: {
                projectId: task.projectId,
                assessmentId: task.assessmentId,
                bundleId: task.controlBundleId,
              },
              state,
            },
            highlight
          );
          break;

        case GQL.ProjectType.FunctionalModel:
          if (task.parentAISystem) {
            functionalModelBundleHelper.navigateTo(
              'BUNDLE_FORM',
              {
                params: {
                  projectId: task.parentAISystem,
                  functionalModelId: task.projectId,
                  assessmentId: task.assessmentId,
                  bundleId: task.controlBundleId,
                },
                state,
              },
              highlight
            );
          }
          break;

        case GQL.ProjectType.ModelCandidate:
        case GQL.ProjectType.ModelChampion:
        case GQL.ProjectType.Dataset:
          if (task.parentAISystem && task.functionalModel) {
            candidateModelBundleHelper.navigateTo(
              'BUNDLE_FORM',
              {
                params: {
                  projectId: task.parentAISystem,
                  functionalModelId: task.functionalModel,
                  candidateId: task.projectId,
                  assessmentId: task.assessmentId,
                  bundleId: task.controlBundleId,
                },
                state,
              },
              highlight
            );
          }
          break;

        case GQL.ProjectType.AgentCandidate:
          if (task.parentAISystem && task.functionalModel) {
            candidateAgentBundleHelper.navigateTo(
              'BUNDLE_FORM',
              {
                params: {
                  projectId: task.parentAISystem,
                  functionalModelId: task.functionalModel,
                  agentId: task.projectId,
                  assessmentId: task.assessmentId,
                  bundleId: task.controlBundleId,
                },
                state,
              },
              highlight
            );
          }
          break;

        case GQL.ProjectType.VendorModel:
          vendorModelBundleHelper.navigateTo(
            'BUNDLE_FORM',
            {
              params: {
                vendorModelId: task.projectId,
                assessmentId: task.assessmentId,
                bundleId: task.controlBundleId,
              },
              state,
            },
            highlight
          );
          break;

        case GQL.ProjectType.VendorAgent:
          vendorAgentBundleHelper.navigateTo(
            'BUNDLE_FORM',
            {
              params: {
                vendorAgentId: task.projectId,
                assessmentId: task.assessmentId,
                bundleId: task.controlBundleId,
              },
              state,
            },
            highlight
          );
          break;
      }
    } catch (err) {
      console.error('Error navigating to control task:', err);
    }
  };

  const columns: TableColumn<GQL.ControlTask>[] = [
    {
      key: 'controlName',
      label: (
        <Text sz="body14" color="secondary">
          Question
        </Text>
      ),
      width: '30%',
      render: (_, task) => (
        <Box>
          <Text sz="body14">{task.controlName}</Text>
          <Text sz="body12" color="secondary">
            {task.controlIdentifier}
          </Text>
        </Box>
      ),
      cellClassName: 'table-cell table-cell-w-30',
    },
    {
      key: 'projectName',
      label: (
        <Text sz="body14" color="secondary">
          Project
        </Text>
      ),
      width: '18%',
      render: (_, task) => <Text sz="body14">{task.projectName}</Text>,
      cellClassName: 'table-cell table-cell-w-18',
    },
    {
      key: 'assessmentName',
      label: (
        <Text sz="body14" color="secondary">
          Assessment
        </Text>
      ),
      width: '18%',
      render: (_, task) => <Text sz="body14">{task.assessmentName}</Text>,
      cellClassName: 'table-cell table-cell-w-18',
    },
    {
      key: 'assignedTo',
      label: (
        <Text sz="body14" color="secondary">
          Assigned To
        </Text>
      ),
      width: '14%',
      render: (_, task) => (
        <Text sz="body14">{task.assignedToName ?? task.assignedTo}</Text>
      ),
      cellClassName: 'table-cell table-cell-w-14',
    },
    {
      key: 'dueDate',
      label: (
        <Text sz="body14" color="secondary">
          Due Date
        </Text>
      ),
      width: '10%',
      render: (_, task) => (
        <Text sz="body14">
          {task.dueDate ? new Date(task.dueDate).toLocaleDateString() : '-'}
        </Text>
      ),
      cellClassName: 'table-cell table-cell-w-10',
    },
    {
      key: 'status',
      label: (
        <Text sz="body14" color="secondary">
          Status
        </Text>
      ),
      width: '10%',
      align: 'center',
      render: (_, task) => (
        <Chip
          className="status-chip"
          data-status={task.status}
          label={<Text sz="body14">{controlStatusLabel(task.status)}</Text>}
          variant="filled"
          size="small"
        />
      ),
      cellClassName: 'table-cell table-cell-w-10',
    },
    {
      key: 'actions',
      label: '',
      width: '4%',
      align: 'right',
      render: () => <Button icon={ArrowRight} />,
      cellClassName: 'table-cell table-cell-w-4',
    },
  ];

  return (
    <div className={className}>
      <GenericTableNew
        columns={columns}
        data={tasks}
        getRowKey={(task) => task.taskId}
        onRowClick={handleRowClick}
        rowClassName="table-row"
        className="table"
        usePaper={false}
        containerClassName=""
        showColumnBorders={false}
        showRowBorders
        headerBackground="light"
      />
    </div>
  );
};

const ControlTaskTableView = styled(ControlTaskTableViewImpl)(({ theme }) => {
  const t = getDesignTokens(theme.palette.mode, theme.accentPalette);
  return css`
    & .MuiChip-label {
      text-transform: capitalize;
    }

    .status-chip {
      &[data-status='PENDING'] {
        background-color: ${t.surface2};
        color: ${t.textPrimary};
      }
      &[data-status='COMPLETED'] {
        background-color: ${theme.palette.success.main};
        color: ${t.status.success};
      }
      &[data-status='CANCELLED'] {
        background-color: ${t.risk.highBg};
        color: ${t.status.danger};
      }
    }

    .table {
      width: 100%;
      table-layout: fixed;
      border-collapse: collapse;
      margin: 0;
      border-spacing: 0;
    }

    ${theme.breakpoints.down('md')} {
      min-width: 1000px;
    }

    ${theme.breakpoints.up('md')} {
      min-width: auto;
    }

    .table-cell {
      padding: ${theme.spacing(3)};
      white-space: normal;
      word-wrap: break-word;
      vertical-align: middle;
    }

    .table-cell-w-30 {
      width: 30%;
      padding-left: ${theme.spacing(3)};
    }
    .table-cell-w-18 {
      width: 18%;
    }
    .table-cell-w-14 {
      width: 14%;
    }
    .table-cell-w-10 {
      width: 10%;
    }
    .table-cell-w-4 {
      width: 4%;
    }
    .table-row {
      cursor: pointer;
      &:hover {
        background-color: ${t.surface2};
      }
    }
  `;
});

export default ControlTaskTableView;
